import { sectionKind } from "../config/turnoverLayout";
import { evaluateRequirements } from "../config/contractRequirements";
import { normalizeKickoff, kickoffDates, KICKOFF_PARTS, PUBLIC_REFERENCE_LABEL } from "../config/marketingKickoff";
import { labelFor } from "../config/turnoverOptions";
import { outcomeLabel, ISSUE_STATUS, isMustResolve, exceptionOutstanding, exceptionDateText } from "../pages/turnover/contractIssues";
import { hasReviewChecklist, reviewItemsFor } from "../config/contractReviewItems";
import { contractMarkdownToEmailHtml } from "./emailMarkdown.js";
import { fmtPhone } from "../pages/turnover/shared.js";
import { isNoCommission } from "../pages/turnover/commission";

// ── Turnover Package — printable HTML summary ─────────────────────────────────
// Builds a styled, print-optimized HTML document and opens it in a new tab
// with the browser print dialog. "Save as PDF" in the print dialog produces
// the distributable packet — real typography, native Unicode, page-break
// control, zero PDF-library layout code.
//
// Uploaded documents are NOT embedded — they live in the Construction
// SharePoint job folder; this packet is the index + narrative distributed at
// the turnover meeting.

function esc(v) {
  return String(v ?? "").replace(/[&<>"']/g, c => ({
    "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;",
  }[c]));
}
function fmtMoney(v) {
  const n = parseFloat(v);
  return isNaN(n) ? "—" : "$" + n.toLocaleString("en-US", { maximumFractionDigits: 0 });
}
function fmtDate(v) {
  if (!v) return "—";
  const s = String(v);
  const d = /^\d{4}-\d{2}-\d{2}$/.test(s) ? new Date(s + "T00:00:00") : new Date(s);
  return isNaN(d) ? esc(v) : d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}
function parseJson(v, fallback) {
  try { const p = JSON.parse(v); return p ?? fallback; } catch { return fallback; }
}

function kvRows(pairs) {
  return pairs
    .filter(([, val]) => val !== null && val !== undefined && val !== "")
    .map(([label, val]) => `<div class="kv"><div class="kv-l">${esc(label)}</div><div class="kv-v">${esc(val)}</div></div>`)
    .join("");
}
function tableHtml(headers, rows, widths = []) {
  if (!rows.length) return "";
  const ths = headers.map((h, i) =>
    `<th${widths[i] ? ` style="width:${widths[i]}"` : ""}>${esc(h)}</th>`).join("");
  const trs = rows.map(r =>
    `<tr>${r.map(c => `<td>${c}</td>`).join("")}</tr>`).join("");
  return `<table><thead><tr>${ths}</tr></thead><tbody>${trs}</tbody></table>`;
}
function statusBadge(sec) {
  if (!sec.IsApplicable) return `<span class="badge badge-na">N/A</span>`;
  if (sec.IsComplete)    return `<span class="badge badge-done">Complete</span>`;
  return `<span class="badge badge-open">Open</span>`;
}

// opts.view = "app": the in-app package page (/turnover/:id/package, 2026-09-24)
// — same content without the toolbar, the email panel or its script; section
// titles link back to the turnover and document links open a flyout there
// (data-doc-id). The printed / emailed package keeps the full document.
export function buildTurnoverPackageHtml(data, opts = {}) {
  const app = opts.view === "app";
  const {
    turnover: t, sections = [], documents = [], actionItems = [],
    schedule = [], contractReview = [], aiSummaries = [],
    hsCompanies = [], hsContacts = [],
    hsLineItems = [], permitsTerms = null, contractRequirements = [], generatedBy = "", directoryUsers = [],
    standingRecipients, marketingKickoff = null, optionRows = [], contractIssues = [], contractReviewItems = [],
  } = data;

  // Assemble AI contract review text (combined synthesis, then fall back to per-doc sections)
  function getContractAiHtml() {
    // Prefer the combined synthesis entry
    const combinedSecs = aiSummaries
      .filter(s => s.SummaryType?.startsWith("contract_review_combined_s"))
      .sort((a, b) => {
        const na = parseInt((a.SummaryType || "").match(/_s(\d+)$/)?.[1] || 0);
        const nb = parseInt((b.SummaryType || "").match(/_s(\d+)$/)?.[1] || 0);
        return na - nb;
      });
    const perDocSecs = aiSummaries
      .filter(s => s.SummaryType?.startsWith("contract_review_doc_") && !s.SummaryType.includes("_combined"))
      .sort((a, b) => {
        const na = parseInt((a.SummaryType || "").match(/_s(\d+)$/)?.[1] || 0);
        const nb = parseInt((b.SummaryType || "").match(/_s(\d+)$/)?.[1] || 0);
        return na - nb;
      });
    const secs = combinedSecs.length ? combinedSecs : perDocSecs;
    if (!secs.length) return "";
    const rawText = secs.map(s => {
      let text = s.SummaryText || "";
      // Strip old JSON wrapper format
      const jsonStart = text.indexOf("{");
      if (jsonStart !== -1) {
        try { const j = JSON.parse(text.slice(jsonStart)); text = j.markdown || j.text || text; } catch {}
      }
      return text;
    }).join("\n\n");
    if (!rawText.trim()) return "";
    // Use contractMarkdownToEmailHtml for inline-styled output safe for print
    return contractMarkdownToEmailHtml(rawText);
  }

  const genStamp = new Date().toLocaleString("en-US", {
    timeZone: "America/New_York", month: "long", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit",
  });
  const subs   = parseJson(t.SubcontractedEntitiesJson, []);
  const splits = parseJson(t.CommissionSplitsJson, []);
  const ordered = [...sections].sort((a, b) => a.SectionNumber - b.SectionNumber);

  // ── Cover blocks ────────────────────────────────────────────────────────────
  const identity = kvRows([
    ["Client",             t.ClientName],
    ["HubSpot Deal #",     t.HubSpotDealID],
    ["Salesperson",        t.Salesperson],
    ["Estimator",          t.Estimator],
    ["Project Manager",    t.ProjectManager],
    ["Planned Turnover",   fmtDate(t.PlannedTurnoverDate)],
    ["Market / Type",      [t.Market, t.ProjectType].filter(Boolean).join("  /  ")],
    ["Contract",           t.ContractStructure],
    ["Labor",              t.LaborStrategy],
    ["Contract Value",     fmtMoney(t.ContractValue || t.DealAmount)],
    ["Square Feet",        t.SquareFeet ? `${parseInt(t.SquareFeet).toLocaleString()} SF` : null],
    ["Contracting Entity", t.ContractingEntity],
    // Job number requested through the administrator exception, contract not yet recorded.
    ["Executed Contract", exceptionOutstanding(t)
      ? `TO FOLLOW — expected ${exceptionDateText(t.JobNumberExceptionExpectedDate)}. Job number requested early: ${t.JobNumberExceptionReason || "—"} (approved by ${t.JobNumberExceptionBy || "—"})`
      : null],
    ["Also Subcontracted", subs.length ? subs.join(", ") : null],
  ]);

  const splitsTable = isNoCommission(splits) ? `<p style="margin:0">Not applicable — no commission is paid on this job.</p>` : splits.length ? tableHtml(
    ["Name", "Role", "Split %"],
    splits.map(s => [esc(s.name), esc(s.role), s.split_pct != null ? esc(s.split_pct) + "%" : ""]),
    ["45%", "35%", "20%"]
  ) : "";

  const releaseTable = hsLineItems.length ? tableHtml(
    ["Line Item", "Amount", "Status", "Release Date"],
    hsLineItems.map(li => [
      esc(li.name),
      fmtMoney(li.amount || li.price),
      (li.awarded === "true" || li.awarded === true)
        ? `<span class="badge badge-done">Released</span>`
        : `<span class="badge badge-open">Forecasted</span>`,
      fmtDate(li.release_date),
    ]),
    ["40%", "20%", "20%", "20%"]
  ) : "";

  const companiesTable = hsCompanies.length ? tableHtml(
    ["Role", "Company", "Location"],
    hsCompanies.map(c => {
      const cityStateZip = [c.city, c.state, c.zip].filter(Boolean).join(", ");
      const fullAddress  = [c.address, cityStateZip].filter(Boolean).join("<br>");
      return [esc(c.label), esc(c.name), fullAddress];
    }),
    ["25%", "35%", "40%"]
  ) : "";

  const contactsTable = hsContacts.length ? tableHtml(
    ["Role", "Name", "Email", "Phone"],
    hsContacts.map(c => [esc(c.label), esc(c.name), esc(c.email), esc(fmtPhone(c.phone))]),
    ["22%", "26%", "32%", "20%"]
  ) : "";


  // ── Per-section detail ──────────────────────────────────────────────────────
  const sectionBlocks = ordered.filter(s => s.IsApplicable).map(sec => {
    const n = sec.SectionNumber;
    const secDocs = documents.filter(d => d.SectionID === sec.SectionID);
    let inner = "";

    if (n === 2 && contractReview.length) {
      inner += tableHtml(
        ["Department", "Assigned To", "Status", "Comments"],
        contractReview.map(r => [
          `<strong>${esc(r.Department)}</strong>`, esc(r.AssignedTo),
          r.Status === "Rejected"
            ? `<span class="badge badge-rej">${esc(r.Status)}</span>`
            : r.Status === "Pending"
              ? `<span class="badge badge-open">${esc(r.Status)}</span>`
              : `<span class="badge badge-done">${esc(r.Status)}</span>`,
          esc(r.Comments),
        ]),
        ["18%", "24%", "22%", "36%"]
      );
      if (hasReviewChecklist(t)) {
        const mark = { ok: "✓", issue: "⚠", na: "N/A" };
        const rowsHtml = contractReview.map(r => {
          const items = reviewItemsFor(r, optionRows, contractReviewItems);
          if (!items.length) return null;
          return [`<strong>${esc(r.Department)}</strong>`,
            items.map(i => i.answer ? `${mark[i.answer]} ${esc(i.label)}` : `<span style="color:#b45309">○ ${esc(i.label)}</span>`).join(" &nbsp;·&nbsp; ")];
        }).filter(Boolean);
        if (rowsHtml.length) inner += `<div class="doc-label">What each department checked (✓ OK · ⚠ issue · N/A not in contract · ○ not answered)</div>` + tableHtml(["Department", "Checklist"], rowsHtml, ["18%", "82%"]);
      }
      if (contractIssues.length) {
        const dept = new Map(contractReview.map(r => [r.ReviewID, r.Department]));
        inner += `<div class="doc-label">Contract issues and agreed outcomes</div>` + tableHtml(
          ["Department", "Issue", "Status", "Outcome agreed with the issuer"],
          contractIssues.map(i => [
            esc(dept.get(i.ReviewID) || ""),
            esc(i.Description) + (isMustResolve(i) ? ` <span class="badge badge-open">Must resolve</span>` : ""),
            esc(ISSUE_STATUS[i.Status]?.label || i.Status),
            i.ResolutionOutcome ? `<strong>${esc(outcomeLabel(i.ResolutionOutcome))}</strong>${i.ResolutionNotes ? `<br>${esc(i.ResolutionNotes)}` : ""}` : "—",
          ]),
          ["16%", "38%", "18%", "28%"]
        );
      }
      const aiHtml = getContractAiHtml();
      if (aiHtml) {
        inner += `<div style="margin-top:16px;padding-top:14px;border-top:1px solid #e5e7eb;">
          <div style="font-size:11px;font-weight:700;color:#6b7280;text-transform:uppercase;letter-spacing:0.06em;margin-bottom:10px;">AI Contract Analysis</div>
          ${aiHtml}
        </div>`;
      }
    }
    if (n === 14) {
      const corrCombined = aiSummaries
        .filter(s => s.SummaryType?.startsWith('correspondence_combined_s') && s.SectionID === sec.SectionID)
        .sort((a, b) => {
          const na = parseInt((a.SummaryType || '').match(/_s(\d+)$/)?.[1] || 0);
          const nb = parseInt((b.SummaryType || '').match(/_s(\d+)$/)?.[1] || 0);
          return na - nb;
        })
        .map(s => s.SummaryText).join('\n\n');
      if (corrCombined) {
        inner += `<div style="margin-bottom:14px;padding:12px 14px;background:#f0f6ff;border-left:3px solid #1e40af;border-radius:4px;">
          <div style="font-size:10px;font-weight:700;color:#1e40af;margin-bottom:6px;text-transform:uppercase;letter-spacing:0.05em;">AI Correspondence Summary</div>
          <div style="font-size:12px;color:#1f2937;line-height:1.6;white-space:pre-wrap;">${esc(corrCombined)}</div>
        </div>`;
      }
    }
    if (n === 9) {
      const scheduleSummary = aiSummaries.find(s => s.SummaryType === 'schedule_summary');
      if (scheduleSummary) {
        inner += `<div style="margin-bottom:14px;padding:12px 14px;background:#f0f6ff;border-left:3px solid #1e40af;border-radius:4px;">
          <div style="font-size:10px;font-weight:700;color:#1e40af;margin-bottom:6px;text-transform:uppercase;letter-spacing:0.05em;">AI Schedule Summary</div>
          <div style="font-size:12px;color:#1f2937;line-height:1.6;white-space:pre-wrap;">${esc(scheduleSummary.SummaryText)}</div>
        </div>`;
      }
    }
    if (n === 9 && schedule.length) {
      const hasNotes = schedule.some(m => m.Notes);
      inner += tableHtml(
        hasNotes ? ["", "Milestone", "Date", "Notes"] : ["", "Milestone", "Date"],
        schedule.map(m => {
          const csFlag = (m.IsCSRelevant !== false && m.IsCSRelevant !== 0)
            ? `<span style="display:inline-block;background:#dbeafe;color:#1e40af;font-size:9px;font-weight:700;padding:1px 5px;border-radius:100px;white-space:nowrap;">CS</span>`
            : "";
          const row = [
            csFlag,
            esc(m.MilestoneLabel),
            m.MilestoneDate ? `<strong>${fmtDate(m.MilestoneDate)}</strong>` : "—",
          ];
          if (hasNotes) row.push(esc(m.Notes || ""));
          return row;
        }),
        hasNotes ? ["4%", "46%", "20%", "30%"] : ["4%", "66%", "30%"]
      );
    }
    const kind = sectionKind(t, n);
    if (kind === "requirements") {
      const ev = evaluateRequirements(contractRequirements, documents.filter(d => d.SectionID === sec.SectionID));
      inner += tableHtml(
        ["Requirement", "Applies", "Responsible / details", "Document"],
        ev.items.map(i => [
          esc(i.def.label),
          i.status === "unanswered" ? "<em>Not answered</em>" : i.status === "not_applicable" ? "No" : "Yes",
          esc([i.row?.Responsibility, ...Object.values(i.row?.details || {})].filter(Boolean).join(" · ")),
          i.def.docType && (i.status === "complete" || i.status === "incomplete") ? (i.hasDocument ? `✓ ${esc(i.def.docType)}` : `Missing: ${esc(i.def.docType)}`) : "",
        ]),
        ["26%", "10%", "40%", "24%"]
      );
    }
    if (kind === "marketing") {
      const k = normalizeKickoff(marketingKickoff);
      const yn = (v) => ({ yes: "Yes", no: "No", tbd: "TBD" }[v] || "");
      const picked = (listKeys, values) => listKeys.flatMap(lk => Object.keys(values).filter(key => values[key]).filter(key => labelFor(lk, key, optionRows) !== key).map(key => labelFor(lk, key, optionRows)));
      const signs = picked(["mk_signage_branding", "mk_signage_construction", "mk_signage_marketing"], k.signs);
      const dates = kickoffDates(k, schedule);
      const photos = dates.milestones.filter(m => k.photoMilestones[String(m.id ?? m.label)]).map(m => `${m.label} (${fmtDate(m.date)})`);
      inner += kvRows([
        ["Parts done", KICKOFF_PARTS.map(p => `${k.partsDone[p.key] ? "✓" : "○"} ${p.label}`).join(" · ")],
        ["Signage required", yn(k.signageRequired)],
        ["Signs", k.signageRequired === "no" ? "" : signs.join("; ")],
        ["Hard hat decals", [yn(k.decalsRequired), k.decalType, k.estimatedWorkers ? `${k.estimatedWorkers} workers` : "", k.decalsNumbered === "yes" ? "numbered" : ""].filter(Boolean).join(" · ")],
        ["Signage notes", k.signageNotes],
        ["Content opportunities", picked(["mk_content"], k.opportunities).join("; ")],
        ["Photography budget approved", yn(k.photographyBudgetApproved)],
        ["Content notes", k.contentNotes],
        // Only what §18 adds: the description when Marketing edited it (else
        // it is the Scope Description at the top), dates only when typed here
        // (else they are §9's, listed in §9).
        ["Project description", String(k.projectDescription || "").trim() && k.projectDescription.trim() !== String(t.DealDescription || "").trim() ? k.projectDescription : ""],
        ["Notable features", k.notableFeatures],
        ["Existing imagery", yn(k.imageryAvailable)],
        ["Estimated start / completion", [!dates.startFrom && dates.start && fmtDate(dates.start), !dates.completionFrom && dates.completion && fmtDate(dates.completion)].filter(Boolean).join(" → ")],
        ["Photo milestones", photos.join("; ")],
        ["Marketing follow-ups", k.followUps],
      ]);
    }
    if (kind === "permits" && permitsTerms) {
      inner += kvRows([
        ["Permits",             permitsTerms.permits],
        ["P&P Bonds",           permitsTerms.bonds],
        ["Liquidated Damages",  permitsTerms.ld],
        ["LD Terms",            permitsTerms.ld === "Applicable" ? permitsTerms.ld_terms : null],
        ["AHJ Requirements",    permitsTerms.ahj],
        ["AHJ Notes",           permitsTerms.ahj_notes],
      ]);
    } else if (kind !== "permits" && sec.Notes) {
      inner += `<div class="notes">${esc(sec.Notes).replace(/\n/g, "<br>")}</div>`;
    }

    if (secDocs.length) {
      inner += `<div class="doc-label">Documents</div>` + tableHtml(
        ["Type", "File", "Date", "Value", "Uploaded By"],
        secDocs.map(d => [
          esc(d.DocType),
          d.SharePointUrl
            ? `<a href="${esc(d.SharePointUrl)}" target="_blank" data-doc-id="${esc(d.TurnoverDocumentID)}" style="color:#1d4ed8;text-decoration:none;">${esc(d.FileName)}</a>`
            : esc(d.FileName),
          fmtDate(d.DocDate),
          d.DocValue ? fmtMoney(d.DocValue) : "", esc(d.UploadedBy),
        ]),
        ["16%", "40%", "14%", "14%", "16%"]
      );
    } else if (n !== 1 && !inner) {
      inner += `<div class="empty">No documents or notes for this section.</div>`;
    }

    return `
      <div class="section-block" id="sec-${n}">
        <div class="section-head">
          <div class="section-title">${n}.&nbsp; ${esc(sec.SectionName)}${app ? ` <a href="/turnover/${esc(t.TurnoverID)}?section=${n}" target="_top" class="sec-open">Open in the turnover ↗</a>` : ""}</div>
          <div class="section-meta">${statusBadge(sec)}${sec.AssignedTo ? `<span class="owner">Owner: ${esc(sec.AssignedTo)}</span>` : ""}</div>
        </div>
        ${inner}
      </div>`;
  }).join("");

  // Recipients seeded from the Contract Review sign-off matrix, plus the
  // standing distribution list — editable on the Turnover Configuration admin
  // page (TurnoverConfigContacts, GroupKey='package_email'); this hardcoded
  // array is only the fallback for a caller that doesn't pass one.
  const STANDING_RECIPIENTS = standingRecipients || [
    { name: "Blake Williams", email: "bwilliams@cleanspaceus.com", dept: "Standing" },
  ];
  const reviewRecipients = (() => {
    const seen = new Set();
    const base = [
      ...contractReview
        .filter(r => r.AssignedEmail && r.AssignedEmail.trim())
        .map(r => ({ name: r.AssignedTo || r.AssignedEmail, email: r.AssignedEmail.trim(), dept: r.Department || "" })),
      ...STANDING_RECIPIENTS,
    ];
    return base.filter(r => {
      const k = r.email.toLowerCase();
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });
  })();

  // Typeahead directory for the "add recipients" field. This package is for
  // internal distribution, so it's built from the same company-wide Entra
  // directory (`directoryUsers`) already used for the Project Manager and
  // Contract Review "Assigned To" dropdowns elsewhere on this page — not a
  // second, redundant list. The sign-off roster and HubSpot deal contacts
  // (occasionally an external client/GC contact needs to be looped in) are
  // appended after, deduped against it.
  const contactDirectory = (() => {
    const seen = new Set();
    const base = [
      ...directoryUsers
        .filter(u => u.email && u.email.trim())
        .map(u => ({ name: u.label || u.email, email: u.email.trim(), role: u.department || "" })),
      ...reviewRecipients.map(r => ({ name: r.name, email: r.email, role: r.dept || "" })),
      ...hsContacts
        .filter(c => c.email && c.email.trim())
        .map(c => ({ name: c.name || c.email, email: c.email.trim(), role: c.label || "" })),
    ];
    return base.filter(r => {
      const k = r.email.toLowerCase();
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });
  })();

  // ── Document ────────────────────────────────────────────────────────────────
  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<title>Turnover Package — ${esc(t.JobNumber || "")} ${esc(t.ProjectName || "")}</title>
<style>
  :root { --navy:#1e3a5f; --text:#212529; --grey:#6b7280; --line:#e5e7eb; --soft:#f8f9fb; }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  html { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  body { font-family: "Segoe UI", Arial, Helvetica, sans-serif; color: var(--text); font-size: 12px; line-height: 1.45; }
  .page { max-width: 8.5in; margin: 0 auto; padding: 0 0.25in; }

  .toolbar { position: sticky; top: 0; background: #fff; border-bottom: 1px solid var(--line);
    padding: 10px 16px; display: flex; justify-content: space-between; align-items: center; z-index: 10; }
  .toolbar .btns { display: flex; gap: 10px; }
  .toolbar button { background: var(--navy); color: #fff; border: 0; border-radius: 6px;
    padding: 8px 22px; font-size: 13px; font-weight: 700; cursor: pointer; }
  .toolbar button.secondary { background: #fff; color: var(--navy); border: 1.5px solid var(--navy); }
  .toolbar .hint { color: var(--grey); font-size: 11px; }

  .email-panel { display: none; background: var(--soft); border-bottom: 1px solid var(--line);
    padding: 14px 20px; }
  .email-panel.open { display: block; }
  .email-panel h3 { color: var(--navy); font-size: 13px; margin-bottom: 8px; }
  .email-panel .recips { display: flex; flex-wrap: wrap; gap: 6px 18px; margin-bottom: 10px; }
  .email-panel label { display: flex; align-items: center; gap: 6px; font-size: 12px; cursor: pointer; }
  .email-panel label .dept { color: var(--grey); font-size: 10px; }
  .email-panel input[type=text], .email-panel textarea { width: 100%; padding: 7px 10px;
    border: 1px solid var(--line); border-radius: 6px; font-size: 12px; font-family: inherit;
    margin-bottom: 8px; background: #fff; }
  .email-panel textarea { resize: vertical; min-height: 44px; }

  .ac-wrap { position: relative; margin-bottom: 8px; }
  .chip-input-wrap { display: flex; flex-wrap: wrap; align-items: center; gap: 6px;
    width: 100%; min-height: 36px; padding: 5px 8px; border: 1px solid var(--line);
    border-radius: 6px; background: #fff; cursor: text; }
  .chip-input-wrap:focus-within { border-color: var(--navy); }
  .chip { display: inline-flex; align-items: center; gap: 5px; background: #e8eef7;
    color: var(--navy); border-radius: 100px; padding: 3px 6px 3px 10px; font-size: 12px;
    font-weight: 600; white-space: nowrap; }
  .chip .chip-remove { cursor: pointer; font-size: 13px; line-height: 1; color: #5878a0; padding: 0 2px; }
  .chip .chip-remove:hover { color: #1e293b; }
  .email-panel .chip-input-wrap input[type=text] { border: 0; padding: 4px 2px; margin-bottom: 0;
    width: auto; flex: 1 1 120px; min-width: 120px; background: transparent; }
  .ac-dropdown { position: absolute; top: 100%; left: 0; right: 0; margin-top: 2px;
    background: #fff; border: 1px solid var(--line); border-radius: 6px;
    box-shadow: 0 6px 18px rgba(0,0,0,0.12); max-height: 220px; overflow-y: auto;
    z-index: 20; display: none; }
  .ac-dropdown.open { display: block; }
  .ac-item { padding: 7px 12px; font-size: 12px; cursor: pointer; }
  .ac-item .ac-name { font-weight: 600; color: var(--text); }
  .ac-item .ac-email { font-size: 10.5px; color: var(--grey); margin-left: 6px; }
  .ac-item.active, .ac-item:hover { background: var(--soft); }
  .email-panel .meeting-section { margin: 6px 0 10px; padding: 10px 14px;
    background: #fff; border: 1px solid var(--line); border-radius: 8px; }
  .email-panel .meeting-toggle { display: flex; align-items: center; gap: 8px;
    font-size: 12px; font-weight: 600; cursor: pointer; color: var(--navy); }
  .email-panel .meeting-toggle input[type=checkbox] { width: 15px; height: 15px; cursor: pointer; }
  .email-panel .meeting-row { display: flex; gap: 12px; margin-top: 10px; }
  .email-panel .meeting-col { flex: 1; display: flex; flex-direction: column; gap: 3px; }
  .email-panel .meeting-col label { font-size: 10px; font-weight: 700; color: var(--grey);
    text-transform: uppercase; letter-spacing: 0.04em; }
  .email-panel .meeting-col input, .email-panel .meeting-col select {
    padding: 6px 8px; border: 1px solid var(--line); border-radius: 6px;
    font-size: 12px; font-family: inherit; background: #fff; }
  #meetingLocation { margin-top: 8px; }
  .email-panel .row button { background: var(--navy); color: #fff; border: 0; border-radius: 6px;
    padding: 8px 22px; font-size: 13px; font-weight: 700; cursor: pointer; }
  .email-panel .row button:disabled { opacity: 0.6; cursor: default; }
  .email-panel .status { font-size: 12px; font-weight: 600; }
  .email-panel .status.ok { color: #065f46; }
  .email-panel .status.err { color: #991b1b; }

  .cover { background: var(--navy); color: #fff; padding: 30px 34px; border-radius: 0 0 10px 10px;
    display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 20px; }
  .cover h1 { font-size: 21px; letter-spacing: 0.04em; }
  .cover .proj { color: #b8d0f0; font-size: 15px; font-weight: 600; margin-top: 6px; }
  .cover .job { font-size: 26px; font-weight: 800; font-family: Consolas, monospace; }
  .cover .gen { color: #8fb3dd; font-size: 10px; margin-top: 8px; text-align: right; }

  h2 { color: var(--navy); font-size: 14px; border-bottom: 2px solid var(--navy);
    padding-bottom: 4px; margin: 22px 0 10px; }
  .kv { display: flex; padding: 3px 0; border-bottom: 1px solid #f1f3f5; }
  .kv-l { width: 190px; flex-shrink: 0; font-size: 9.5px; font-weight: 700; color: var(--grey);
    text-transform: uppercase; letter-spacing: 0.05em; padding-top: 2px; }
  .kv-v { flex: 1; }

  table { width: 100%; border-collapse: collapse; margin: 6px 0 14px; }
  th { text-align: left; font-size: 9.5px; font-weight: 700; color: var(--grey);
    text-transform: uppercase; letter-spacing: 0.05em; padding: 5px 8px;
    background: var(--soft); border-bottom: 2px solid var(--line); }
  td { padding: 5px 8px; border-bottom: 1px solid #f1f3f5; vertical-align: top; }
  tr:nth-child(even) td { background: #fcfcfd; }

  .badge { display: inline-block; font-size: 9.5px; font-weight: 700; padding: 1px 9px;
    border-radius: 100px; white-space: nowrap; }
  .badge-done { background: #d1fae5; color: #065f46; }
  .badge-open { background: #fef3c7; color: #92400e; }
  .badge-na   { background: #e5e7eb; color: #6b7280; }
  .badge-rej  { background: #fee2e2; color: #991b1b; }

  .section-block { margin-bottom: 18px; break-inside: avoid-page; }
  .section-head { display: flex; justify-content: space-between; align-items: baseline;
    border-bottom: 2px solid var(--navy); padding-bottom: 3px; margin-bottom: 8px; }
  .section-title { color: var(--navy); font-size: 13px; font-weight: 700; }
  .section-meta { display: flex; align-items: center; gap: 10px; }
  .owner { font-size: 10px; color: var(--grey); }
  .notes { background: var(--soft); border-left: 3px solid var(--navy); padding: 8px 12px;
    margin: 6px 0 12px; border-radius: 0 6px 6px 0; }
  .doc-label { font-size: 9.5px; font-weight: 700; color: var(--grey); text-transform: uppercase;
    letter-spacing: 0.05em; margin-top: 6px; }
  .empty { color: var(--grey); font-style: italic; font-size: 11px; margin: 4px 0 10px; }
  .foot { color: var(--grey); font-size: 10px; margin: 26px 0 40px; border-top: 1px solid var(--line); padding-top: 8px; }

  .page-break { break-before: page; }
  .sec-open { font-size: 11px; font-weight: 600; color: #1d4ed8; text-decoration: none; margin-left: 10px; }
  a[data-doc-id] { cursor: pointer; }
  @media print {
    .toolbar, .email-panel { display: none !important; }
    .cover { border-radius: 0; }
    body { font-size: 11px; }
    @page { margin: 0.6in 0.5in; }
  }
</style>
</head>
<body>
  ${app ? "" : `<div class="toolbar">
    <div class="hint">Use the Print dialog's "Save as PDF" to produce the distributable file.</div>
    <div class="btns">
      <button id="emailToggle">✉ Email Package</button>
      <button onclick="window.print()">🖨 Print / Save as PDF</button>
    </div>
  </div>

  <div class="email-panel" id="emailPanel">
    <h3>Email this package</h3>
    <div class="recips" id="recipList"></div>
    <div class="ac-wrap">
      <div class="chip-input-wrap" id="chipInputWrap">
        <input type="text" id="extraEmailInput" autocomplete="off" placeholder="Add recipients — type a name or email…">
      </div>
      <div class="ac-dropdown" id="acDropdown"></div>
    </div>
    <textarea id="emailNote" placeholder="Optional message to include in the email…"></textarea>

    <div class="meeting-section">
      <label class="meeting-toggle">
        <input type="checkbox" id="createMeeting"> Create Outlook calendar meeting invite
      </label>
      <div id="meetingFields" style="display:none;">
        <div class="meeting-row">
          <div class="meeting-col">
            <label>Meeting date</label>
            <input type="date" id="meetingDate" value="${t.PlannedTurnoverDate ? t.PlannedTurnoverDate.split('T')[0] : ''}">
          </div>
          <div class="meeting-col">
            <label>Start time</label>
            <input type="time" id="meetingStart" value="09:00">
          </div>
          <div class="meeting-col">
            <label>Duration</label>
            <select id="meetingDuration">
              <option value="60">1 hour</option>
              <option value="90">1.5 hours</option>
              <option value="120" selected>2 hours</option>
              <option value="180">3 hours</option>
              <option value="240">4 hours</option>
            </select>
          </div>
        </div>
        <input type="text" id="meetingLocation" placeholder="Location or Teams link (optional)">
      </div>
    </div>

    <div class="row">
      <button id="sendBtn">Send Email</button>
      <span class="status" id="sendStatus"></span>
    </div>
  </div>`}

  <div class="cover">
    <div>
      <h1>TURNOVER PACKAGE</h1>
      <div class="proj">${esc(t.ProjectName || "")}</div>
      ${t.ClientName ? `<div style="color:#8fb3dd;font-size:12px;margin-top:3px;">${esc(t.ClientName)}</div>` : ""}
    </div>
    <div>
      <div class="job">${esc(t.JobNumber || "Pending #")}</div>
      <div class="gen">Generated ${genStamp}${generatedBy ? ` · ${esc(generatedBy)}` : ""}</div>
    </div>
  </div>

  <div class="page">
    <h2>Project Identity</h2>
    ${identity}

    ${t.DealDescription ? `<h2>Scope Description</h2><div class="notes">${esc(t.DealDescription).replace(/\n/g, "<br>")}</div>` : ""}
    ${splitsTable   ? `<h2>Commission Splits</h2>${splitsTable}` : ""}
    ${releaseTable  ? `<h2>Release Schedule (HubSpot Line Items)</h2>${releaseTable}` : ""}
    ${companiesTable? `<h2>Companies</h2>${companiesTable}` : ""}
    ${contactsTable ? `<h2>Contacts</h2>${contactsTable}` : ""}
    ${t.PublicReference ? `<h2>Client Confidentiality</h2>${kvRows([
      ["Public reference", PUBLIC_REFERENCE_LABEL[t.PublicReference] || t.PublicReference],
      ["Confidentiality notes", t.ConfidentialityNotes],
      ["Photography restrictions", t.PhotographyRestrictions],
    ])}` : ""}

    ${(() => {
      const open = actionItems.filter(a => !a.IsComplete);
      if (!open.length) return "";
      return `<h2>Action Items</h2>` + tableHtml(
        ["Action Item", "Assigned To", "Due"],
        open.map(a => [esc(a.Description), esc(a.AssignedTo), fmtDate(a.DueDate)]),
        ["55%", "25%", "20%"]
      );
    })()}

    <div class="empty" style="margin:14px 0 16px;">Every document is saved in the job's SharePoint folder under Turnover, in numbered subfolders matching each section.</div>

    <div class="page-break"></div>
    <h2>Section Detail</h2>
    ${sectionBlocks}

    <div class="foot">
      ${esc(t.JobNumber || "")} · ${esc(t.ProjectName || "")} · Turnover Package generated ${genStamp} by ${esc(generatedBy || "EstAssist")} · EstAssist
    </div>
  </div>
${app ? "</body></html>" : ""}${app ? "" : `
<script>
(function () {
  // Recipients seeded from the Contract Review Sign-Off matrix
  var REVIEW_RECIPIENTS = ${JSON.stringify(reviewRecipients)};
  // Typeahead directory for the "add recipients" field
  var CONTACT_DIRECTORY = ${JSON.stringify(contactDirectory)};

  var panel   = document.getElementById("emailPanel");
  var toggle  = document.getElementById("emailToggle");
  var list    = document.getElementById("recipList");
  var chipWrap    = document.getElementById("chipInputWrap");
  var extraInput  = document.getElementById("extraEmailInput");
  var acDropdown  = document.getElementById("acDropdown");
  var note        = document.getElementById("emailNote");
  var sendBtn     = document.getElementById("sendBtn");
  var status      = document.getElementById("sendStatus");
  var createMtg   = document.getElementById("createMeeting");
  var mtgFields   = document.getElementById("meetingFields");
  var mtgDate     = document.getElementById("meetingDate");
  var mtgStart    = document.getElementById("meetingStart");
  var mtgDuration = document.getElementById("meetingDuration");
  var mtgLocation = document.getElementById("meetingLocation");

  createMtg.addEventListener("change", function () {
    mtgFields.style.display = this.checked ? "block" : "none";
  });

  if (!window.opener) {
    toggle.disabled = true;
    toggle.title = "Email requires the EstAssist tab that opened this window to still be open.";
    toggle.style.opacity = 0.5;
  }

  toggle.addEventListener("click", function () {
    panel.classList.toggle("open");
    // Panel sits in normal document flow right after the sticky toolbar, near
    // the top of the page — if the user has scrolled down into the package
    // (likely, since it can be long), opening it left it out of view with no
    // visual cue, looking like the button did nothing. Auto-scroll it into
    // view on open so this is immediate. Added 2026-07-09.
    if (panel.classList.contains("open")) {
      panel.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  });

  // Render sign-off recipients as pre-checked checkboxes
  REVIEW_RECIPIENTS.forEach(function (r, i) {
    var label = document.createElement("label");
    label.innerHTML =
      '<input type="checkbox" checked data-idx="' + i + '"> ' +
      '<span>' + r.name.replace(/</g, "&lt;") + '</span>' +
      (r.dept ? '<span class="dept">(' + r.dept.replace(/</g, "&lt;") + ')</span>' : '');
    list.appendChild(label);
  });
  if (!REVIEW_RECIPIENTS.length) {
    list.innerHTML = '<span style="color:#6b7280;font-size:11px;">No sign-off recipients found — add emails below.</span>';
  }

  // ── Outlook-style "To" typeahead ────────────────────────────────────────
  // Chips accumulate selected/typed recipients; the text input stays free
  // for the next entry, mirroring Outlook's To-field behavior (type, pick
  // from the dropdown or press Tab/Enter, repeat).
  var chips = [];
  var acMatches = [];
  var acIndex = -1;

  function esc(s) { return String(s).replace(/[&<>"']/g, function (c) {
    return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
  }); }

  function renderChips() {
    chipWrap.querySelectorAll(".chip").forEach(function (el) { el.remove(); });
    chips.forEach(function (c, i) {
      var chip = document.createElement("span");
      chip.className = "chip";
      chip.innerHTML = '<span>' + esc(c.name || c.email) + '</span><span class="chip-remove" data-i="' + i + '">&times;</span>';
      chipWrap.insertBefore(chip, extraInput);
    });
  }
  chipWrap.addEventListener("click", function (e) {
    if (e.target.classList.contains("chip-remove")) {
      chips.splice(parseInt(e.target.dataset.i, 10), 1);
      renderChips();
    } else if (e.target === chipWrap) {
      extraInput.focus();
    }
  });

  function hasChip(email) {
    var k = email.toLowerCase();
    return chips.some(function (c) { return c.email.toLowerCase() === k; });
  }
  function addRecipient(person) {
    if (!person.email || hasChip(person.email)) { extraInput.value = ""; hideDropdown(); return; }
    chips.push(person);
    renderChips();
    extraInput.value = "";
    hideDropdown();
  }
  // Commits whatever free text is left in the input (no dropdown pick) as a
  // literal email, same as Outlook accepting a typed address with no match.
  function commitTyped() {
    var val = extraInput.value.trim().replace(/[,;]+$/, "");
    if (val && val.indexOf("@") > 0) addRecipient({ name: val, email: val });
    extraInput.value = "";
  }

  function getMatches(query) {
    var q = query.toLowerCase();
    return CONTACT_DIRECTORY.filter(function (p) {
      return !hasChip(p.email) &&
        ((p.name && p.name.toLowerCase().indexOf(q) !== -1) || p.email.toLowerCase().indexOf(q) !== -1);
    }).slice(0, 8);
  }
  function renderDropdown() {
    if (!acMatches.length) { hideDropdown(); return; }
    acDropdown.innerHTML = acMatches.map(function (p, i) {
      return '<div class="ac-item' + (i === acIndex ? ' active' : '') + '" data-i="' + i + '">' +
        '<span class="ac-name">' + esc(p.name) + '</span><span class="ac-email">' + esc(p.email) + '</span></div>';
    }).join("");
    acDropdown.classList.add("open");
  }
  function hideDropdown() {
    acMatches = []; acIndex = -1;
    acDropdown.classList.remove("open");
    acDropdown.innerHTML = "";
  }

  extraInput.addEventListener("input", function () {
    var q = extraInput.value.trim();
    if (!q) { hideDropdown(); return; }
    acMatches = getMatches(q);
    acIndex = acMatches.length ? 0 : -1;
    renderDropdown();
  });

  extraInput.addEventListener("keydown", function (e) {
    if (e.key === "ArrowDown" && acMatches.length) {
      e.preventDefault(); acIndex = Math.min(acIndex + 1, acMatches.length - 1); renderDropdown();
    } else if (e.key === "ArrowUp" && acMatches.length) {
      e.preventDefault(); acIndex = Math.max(acIndex - 1, 0); renderDropdown();
    } else if (e.key === "Tab" || e.key === "Enter") {
      if (acMatches.length && acIndex >= 0) {
        e.preventDefault();
        addRecipient(acMatches[acIndex]);
      } else if (extraInput.value.indexOf("@") > 0) {
        e.preventDefault();
        commitTyped();
      }
    } else if (e.key === "," || e.key === ";") {
      e.preventDefault();
      commitTyped();
    } else if (e.key === "Backspace" && !extraInput.value && chips.length) {
      chips.pop();
      renderChips();
    } else if (e.key === "Escape") {
      hideDropdown();
    }
  });

  acDropdown.addEventListener("mousedown", function (e) {
    // mousedown (not click) so this fires before the input's blur handler
    var item = e.target.closest(".ac-item");
    if (!item) return;
    e.preventDefault();
    addRecipient(acMatches[parseInt(item.dataset.i, 10)]);
  });

  extraInput.addEventListener("blur", function () {
    // Give the dropdown's mousedown a chance to run first.
    setTimeout(function () { commitTyped(); hideDropdown(); }, 120);
  });

  sendBtn.addEventListener("click", function () {
    var to = [];
    list.querySelectorAll("input[type=checkbox]:checked").forEach(function (cb) {
      var r = REVIEW_RECIPIENTS[parseInt(cb.dataset.idx, 10)];
      if (r) to.push({ name: r.name, email: r.email });
    });
    commitTyped();
    chips.forEach(function (c) { to.push({ name: c.name, email: c.email }); });
    // De-dupe by email
    var seen = {};
    to = to.filter(function (r) {
      var k = r.email.toLowerCase();
      if (seen[k]) return false;
      seen[k] = true;
      return true;
    });
    if (!to.length) { status.className = "status err"; status.textContent = "Select or add at least one recipient."; return; }
    if (!window.opener) { status.className = "status err"; status.textContent = "EstAssist tab is closed — reopen the package from EstAssist."; return; }

    sendBtn.disabled = true;
    status.className = "status";
    status.textContent = "Sending…";

    var meeting = null;
    if (createMtg.checked && mtgDate.value) {
      meeting = {
        date:     mtgDate.value,
        start:    mtgStart.value || "09:00",
        duration: parseInt(mtgDuration.value, 10) || 120,
        location: mtgLocation.value.trim(),
      };
    }
    // Target only the EstAssist origin (baked in at build time) — never "*",
    // so recipients/notes can't leak to a foreign window holding an opener ref.
    window.opener.postMessage({ type: "estassist-send-package", to: to, note: note.value || "", meeting: meeting }, ${JSON.stringify(window.location.origin)});
  });

  window.addEventListener("message", function (e) {
    if (e.origin !== ${JSON.stringify(window.location.origin)}) return;
    var d = e.data || {};
    if (d.type !== "estassist-package-email-result") return;
    sendBtn.disabled = false;
    if (d.ok) {
      status.className = "status ok";
      var msg = "✓ Sent to " + d.count + " recipient" + (d.count === 1 ? "" : "s") + ".";
      if (d.calendarCreated) msg += "  Calendar invite created.";
      status.textContent = msg;
    } else {
      status.className = "status err";
      status.textContent = "Send failed: " + (d.error || "unknown error");
    }
  });
})();
</script>
</body>
</html>`}`;
}

// Opens the packet in a new tab. The user prints / saves as PDF from there.
export function openTurnoverPackagePrint(data) {
  const html = buildTurnoverPackageHtml(data);
  const win = window.open("", "_blank");
  if (!win) {
    alert("Pop-up blocked — allow pop-ups for this site to open the printable package.");
    return;
  }
  win.document.open();
  win.document.write(html);
  win.document.close();
}
